import React from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import ReactCountryFlag from 'react-country-flag';
import DialogTitle from '@mui/joy/DialogTitle';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { toggleDarkMode } from '../../store/slices/darkModeSlice';
import { Language, setLanguage } from '../../store/slices/languageSlice';
import { IconButton, MenuItem, Select, SelectChangeEvent, ToggleButton, ToggleButtonGroup } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { useTranslation } from 'react-i18next';
import { Currency, fetchCurrencyRate, setCurrency } from '../../store/slices/currencySlice';
import {setSettingsOpen} from '../../store/slices/settingsDrawerSlice';

const languages: { labelKey: string; value: Language; country: string }[] = [
  { labelKey: 'english', value: 'en', country: 'GB' },
  { labelKey: 'german', value: 'de', country: 'DE' },
  { labelKey: 'french', value: 'fr', country: 'FR' },
  { labelKey: 'spanish', value: 'es', country: 'ES' },
  { labelKey: 'brazilian', value: 'br', country: 'BR' },
  { labelKey: 'chinese', value: 'zh', country: 'CN' },
  { labelKey: 'czech', value: 'cs', country: 'CZ' },
  { labelKey: 'hindi', value: 'hi', country: 'IN' },
  { labelKey: 'indonesian', value: 'id', country: 'ID' },
  { labelKey: 'japanese', value: 'ja', country: 'JP' },
  { labelKey: 'korean', value: 'ko', country: 'KR' },
  { labelKey: 'norwegian', value: 'no', country: 'NO' },
  { labelKey: 'polish', value: 'pl', country: 'PL' },
  { labelKey: 'russian', value: 'ru', country: 'RU' },
  { labelKey: 'swedish', value: 'se', country: 'SE' },
  { labelKey: 'thai', value: 'th', country: 'TH' },
  { labelKey: 'turkish', value: 'tr', country: 'TR' },
  { labelKey: 'ukrainian', value: 'ua', country: 'UA' },
];

export const currencies: { value: Currency; symbol: string }[] = [
  { value: 'USD', symbol: '$' },
  { value: 'EUR', symbol: '€' },
  { value: 'GBP', symbol: '£' },
  { value: 'JPY', symbol: '¥' },
  { value: 'AUD', symbol: '$' },
  { value: 'CAD', symbol: '$' },
  { value: 'CHF', symbol: 'CHF' },
  { value: 'CNY', symbol: '¥' },
  { value: 'SEK', symbol: 'kr' },
  { value: 'NZD', symbol: '$' },
  { value: 'BRL', symbol: 'R$' },
  { value: 'CZK', symbol: 'Kč' },
  { value: 'HRN', symbol: '₴' },
  { value: 'IDR', symbol: 'Rp' },
  { value: 'INR', symbol: '₹' },
  { value: 'KRW', symbol: '₩' },
  { value: 'NOK', symbol: 'kr' },
  { value: 'PLN', symbol: 'zł' },
  { value: 'RUB', symbol: '₽' },
  { value: 'THB', symbol: '฿' },
  { value: 'TRY', symbol: '₺' },
];

const NavbarSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const open = useSelector((state: RootState) => state.settingsDrawer.isSettingsOpen);
  const isDarkMode = useSelector((state: RootState) => state.darkMode.isDarkMode);
  const language = useSelector((state: RootState) => state.language.language);
  const currency = useSelector((state: RootState) => state.currency.currency);

  const toggleDrawer = (newOpen: boolean) => () => {
    dispatch(setSettingsOpen(newOpen));
  };

  const changeDarkMode = (value: boolean) => {
    if (value !== null && value !== isDarkMode) {
      dispatch(toggleDarkMode());
    }
  };

  const handleLanguageChange = (event: SelectChangeEvent<Language>) => {
    dispatch(setLanguage(event.target.value as Language));
  };

  const handleCurrencyChange = (event: SelectChangeEvent<Currency>) => {
    const newCurrency = event.target.value as Currency;
    dispatch(setCurrency(newCurrency));
    dispatch(fetchCurrencyRate(newCurrency));
  };

  const DrawerList = (
      <Box
          sx={{
            width: 260,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          role="presentation"
          className="h-full bg-light dark:bg-dark"
      >
        <div className="flex w-full">
          <IconButton
              onClick={toggleDrawer(false)}
              sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <DialogTitle className="font-semibold text-xl text-center text-light-text dark:text-dark-text pb-6">
          {t('settings.title')}
        </DialogTitle>

        <ToggleButtonGroup
            value={isDarkMode}
            exclusive
            onChange={(e, value) => changeDarkMode(value)}
            sx={(theme) => ({
              width: '240px',
              height: '50px',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'}`,
              borderRadius: 1,
              '& .MuiToggleButton-root': {
                padding: '0 12px',
                height: '100%',
                border: 'none',
                borderRadius: 0,
              },
              '& .MuiToggleButton-root:first-of-type': {
                borderTopLeftRadius: 4,
                borderBottomLeftRadius: 4,
              },
              '& .MuiToggleButton-root:last-of-type': {
                borderTopRightRadius: 4,
                borderBottomRightRadius: 4,
              },
            })}
            className="mb-4"
        >
          <ToggleButton value={false} sx={{ width: '120px' }} color="primary">
            <LightModeIcon className={`${isDarkMode ? 'text-white' : 'text-yellow-500'} mr-1`} />
            <div className="text-light-text dark:text-dark-text">
              {t('settings.light_mode')}
            </div>
          </ToggleButton>
          <ToggleButton value={true} sx={{ width: '120px' }} color="primary">
            <DarkModeIcon className={`${isDarkMode ? 'text-yellow-500' : 'text-light-text'} mr-1`} />
            <div className="text-light-text dark:text-dark-text">
              {t('settings.dark_mode')}
            </div>
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Language */}
        <Select<Language>
            value={language}
            onChange={handleLanguageChange}
            sx={{ width: '240px', height: '50px' }}
            className="mb-4"
            variant="outlined"
            size="medium"
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 450,
                },
                className: 'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600',
              },
            }}
        >
          {languages.map((lang) => (
              <MenuItem key={lang.value} value={lang.value}>
                <div className="flex items-center gap-2">
                  <ReactCountryFlag svg countryCode={lang.country} style={{ width: '1.4em', height: '1.4em' }} />
                  <span>{t(`settings.${lang.labelKey}`)}</span>
                </div>
              </MenuItem>
          ))}
        </Select>

        {/* Currency */}
        <Select<Currency>
            value={currency}
            onChange={handleCurrencyChange}
            sx={{ width: '240px', height: '50px' }}
            variant="outlined"
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 450,
                },
                className: 'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600',
              },
            }}
        >
          {currencies.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                <span className="mr-2">{c.value}</span>
                <span>({c.symbol})</span>
              </MenuItem>
          ))}
        </Select>
      </Box>
  );

  return (
      <div className="flex items-center gap-2 min-w-0 overflow-hidden">
        <IconButton
            onClick={toggleDrawer(true)}
            className="text-light-text dark:text-dark-text hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          <MenuIcon />
        </IconButton>
        <Drawer anchor="right" open={open} onClose={toggleDrawer(false)} ModalProps={{ disableScrollLock: true }}>
          {DrawerList}
        </Drawer>
      </div>
  );
};

export default NavbarSettings;